import React from 'react';
import { ActionButton } from '../ActionButton';
import { MID_GRAY } from '@/styles/tokens';
import type { Component } from '@/types';

interface PinNavigateDrilldownProps {
  pin: {
    id: string;
    navigation?: { targetPageId: string; targetPageName?: string };
  };
  component: Component;
  pages: Array<{ id: string; name: string }>;
  currentPageId: string | null;
  updateComponent: (id: string, updates: Partial<Component>) => void;
  onDone: () => void;
}

export const PinNavigateDrilldown: React.FC<PinNavigateDrilldownProps> = ({
  pin,
  component,
  pages,
  currentPageId,
  updateComponent,
  onDone,
}) => {
  const availablePages = (pages ?? []).filter(page => page.id !== currentPageId);

  const handleRemoveNav = () => {
    const currentPins = [...((component.properties as Record<string, unknown>)?.pins as Array<Record<string, unknown>> ?? [])];
    const idx = currentPins.findIndex((p: Record<string, unknown>) => p.id === pin.id);
    if (idx >= 0) {
      const { navigation: _removed, ...rest } = currentPins[idx] as Record<string, unknown>;
      currentPins[idx] = rest;
      updateComponent(component.id, { properties: { ...component.properties, pins: currentPins } });
    }
    onDone();
  };

  const handleSelectPage = (pageId: string, pageName: string) => {
    const currentPins = [...((component.properties as Record<string, unknown>)?.pins as Array<Record<string, unknown>> ?? [])];
    const idx = currentPins.findIndex((p: Record<string, unknown>) => p.id === pin.id);
    if (idx >= 0) {
      currentPins[idx] = {
        ...currentPins[idx],
        navigation: { targetPageId: pageId, targetPageName: pageName },
      };
      updateComponent(component.id, { properties: { ...component.properties, pins: currentPins } });
    }
    onDone();
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
        <ActionButton variant="back" onClick={onDone}>
          ← Back
        </ActionButton>
        <span style={{ fontSize: '13px', color: MID_GRAY.DEFAULT }}>Link to a page:</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {pin.navigation?.targetPageId && (() => {
          const currentTargetName = pin.navigation?.targetPageName
            || pages.find(p => p.id === pin.navigation?.targetPageId)?.name
            || 'Unknown page';
          return (
            <ActionButton key="remove-nav" variant="destructive" onClick={handleRemoveNav}>
              Remove → {currentTargetName}
            </ActionButton>
          );
        })()}
        {availablePages.map(page => {
          const isActive = pin.navigation?.targetPageId === page.id;
          return (
            <ActionButton
              key={page.id}
              variant={isActive ? 'active' : 'default'}
              onClick={() => handleSelectPage(page.id, page.name)}
            >
              {isActive ? `✓ ${page.name}` : page.name}
            </ActionButton>
          );
        })}
        {availablePages.length === 0 && (
          <span style={{ fontSize: '12px', color: MID_GRAY.DEFAULT, padding: '6px 0' }}>
            No other pages available. Create more pages first.
          </span>
        )}
      </div>
    </>
  );
};
